import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import { useSetCompanyNavbarMobileIsOpen } from "components/company/v3/menu/hooks";
import type { MenuRenderProps } from "components/company/v3/menu/types";
import Editor from "components/editor/Editor";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { Button } from "components/ui/Button";
import classNames from "components/ui/classNames";
import { Form, FormField } from "components/ui/Form";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "components/ui/Sheet";
import { Spinner } from "components/ui/Spinner";
import type {
  ISaveIssuerNotesMutation,
  ISaveIssuerNotesMutationVariables,
} from "lib/graphql/mutations/__generated__/SaveIssuerNotesMutation.generated";
import SaveIssuerNotesMutation from "lib/graphql/mutations/SaveIssuerNotesMutation";
import type {
  IUserNotesQuery,
  IUserNotesQueryVariables,
} from "lib/graphql/queries/__generated__/UserNotesQuery.generated";
import UserNotesQuery from "lib/graphql/queries/UserNotesQuery";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FormattedMessage, useIntl } from "react-intl";
import * as z from "zod";

export default function CompanyNotes({ icon, label }: MenuRenderProps) {
  const [open, setIsOpen] = useState(false);
  const setMenuMobileIsOpen = useSetCompanyNavbarMobileIsOpen();

  const issuer = useRootIssuer();
  const intl = useIntl();

  const [saveIssuerNotes, { loading: saveIssuerNotesLoading }] = useMutation<
    ISaveIssuerNotesMutation,
    ISaveIssuerNotesMutationVariables
  >(SaveIssuerNotesMutation);

  const { data, loading } = useQuery<IUserNotesQuery, IUserNotesQueryVariables>(UserNotesQuery, {
    variables: {
      issuerId: issuer._id,
    },
  });

  const form = useForm<UserNotesFormData>({
    mode: "all",
    resolver: zodResolver(UserNotesSchema),
    defaultValues: {
      bodyAsJson: data?.userNotes?.bodyAsJson || null,
      bodyAsPlainText: data?.userNotes?.bodyAsPlainText || null,
    },
  });

  const onSubmit = async (data: UserNotesFormData) => {
    await toast.promise(
      saveIssuerNotes({
        variables: {
          issuerId: issuer._id,
          bodyAsJson: data.bodyAsJson || null,
          bodyAsPlainText: data.bodyAsPlainText || null,
        },
        refetchQueries: [
          {
            query: UserNotesQuery,
            variables: {
              issuerId: issuer._id,
            },
          },
        ],
      }),
      {
        loading: intl.formatMessage({
          defaultMessage: "Salvando...",
          id: "+1gqBc",
        }),
        success: intl.formatMessage({
          defaultMessage: "Suas notas foram salvas!",
          id: "gL92f6",
        }),
        error: intl.formatMessage({
          defaultMessage: "Falha técnica. Não foi possível salvar suas notas. Entre em contato no suporte!",
          id: "pf/kCO",
        }),
      },
    );
    // Reset dirty state
    form.reset(form.getValues());
  };

  useEffect(() => {
    if (data && data.userNotes && !form.formState.isDirty) {
      form.reset({
        bodyAsJson: data.userNotes.bodyAsJson,
        bodyAsPlainText: data.userNotes.bodyAsPlainText,
      });
    }
  }, [data, form]);

  const hasWrittenNotes = (data?.userNotes?.bodyAsPlainText?.trim().length || 0) > 0;

  return (
    <Sheet
      key={issuer._id}
      modal={true}
      open={open}
      onOpenChange={async (open) => {
        if (!open && form.formState.isDirty) {
          await form.handleSubmit(onSubmit)();
        }
        setIsOpen(open);
      }}
    >
      <SheetTrigger onClick={() => setMenuMobileIsOpen(false)}>
        <div
          className={classNames(
            "hidden max-h-[42px] flex-col items-center space-y-0.5 px-1 text-gray-500 dark:text-slate-300 lg:flex",
            {
              "rounded-xl font-bold text-orange-500 dark:border-orange-600 dark:text-orange-300": hasWrittenNotes,
            },
          )}
        >
          <div>{icon}</div>
          <span className="text-xs">{label}</span>
        </div>

        <div
          className={classNames(
            "group flex items-center space-x-4 py-2 tracking-wide text-gray-600",
            "lg:hidden",
            "hover:bg-slate-100 focus:outline-none",
            "rounded-full px-2 dark:text-slate-300 dark:hover:bg-slate-800",
          )}
        >
          <div className={classNames("z-0 flex h-10 w-10 items-center justify-center")}>
            <div
              className={classNames(
                "inline-flex rounded-2xl p-2.5 squircle",
                "border-blue-200 bg-blue-50 text-blue-400",
                "relative border-blue-200 bg-blue-50 text-blue-400 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300",
              )}
            >
              {icon}
            </div>
          </div>

          <span className="text-base font-medium text-slate-600 dark:text-slate-300">{label}</span>
        </div>
      </SheetTrigger>

      <SheetContent
        position="right"
        className="flex h-full w-screen flex-col space-y-4 border-none bg-white dark:bg-slate-900 xl:w-1/3"
        onOpenAutoFocus={(event) => {
          // See: https://github.com/radix-ui/primitives/discussions/935
          event.preventDefault();
          form.setFocus("bodyAsJson");
        }}
      >
        <SheetHeader className="flex flex-row space-x-2 space-y-0">
          <SheetTitle className="text-3xl font-black text-slate-800 dark:text-slate-100">
            <FormattedMessage defaultMessage="Anotações" id="mileDD" />
          </SheetTitle>
          {saveIssuerNotesLoading && <Spinner />}
        </SheetHeader>
        {loading ? (
          <LoadingSkeleton count={1} className="h-full" />
        ) : (
          <Form {...form}>
            <form
              autoComplete="off"
              className="flex h-[calc(100vh-120px)] w-full flex-col space-y-4"
              onSubmit={form.handleSubmit(onSubmit)}
              noValidate={true}
            >
              <FormField
                control={form.control}
                name="bodyAsJson"
                render={({ field }) => {
                  return (
                    <div className="flex w-full flex-col overflow-hidden rounded-xl border border-slate-300 dark:border-slate-950">
                      <Editor
                        className={classNames(
                          "box-border h-full w-full resize-none appearance-none overflow-y-auto",
                          "font-aeonik text-base font-normal tracking-wider sm:text-sm",
                          "border-none",
                          "placeholder:text-slate-400",
                          "ring-inset focus:shadow-md focus:ring-2 focus-visible:ring-slate-500",
                          "disabled:bg-slate-100 disabled:text-slate-600",
                          "focus:border-transparent dark:border-slate-950 dark:bg-slate-800 dark:text-slate-300 dark:placeholder:text-slate-400",
                          "dark:focus-visible:ring-slate-500 dark:focus-visible:placeholder:text-slate-400",
                          "dark:text-white dark:focus:ring-blue-400",
                        )}
                        initialState={field.value || undefined}
                        onChange={(bodyAsJson, bodyAsPlainText) => {
                          field.onChange(bodyAsJson);
                          form.setValue("bodyAsPlainText", bodyAsPlainText);
                        }}
                        onClear={() => {
                          form.reset();
                        }}
                        onSubmit={() => {
                          form.handleSubmit(onSubmit)();
                        }}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Escreva algo aqui...",
                          id: "/ExxQU",
                        })}
                      />
                    </div>
                  );
                }}
              />

              <Button type="submit" className="w-full" disabled={saveIssuerNotesLoading}>
                {saveIssuerNotesLoading ? (
                  <FormattedMessage defaultMessage="Salvando..." id="+1gqBc" />
                ) : (
                  <FormattedMessage defaultMessage="Salvar" id="DFil5M" />
                )}
              </Button>
            </form>
          </Form>
        )}
      </SheetContent>
    </Sheet>
  );
}

const UserNotesSchema = z.object({
  bodyAsJson: z.string().optional().nullable(),
  bodyAsPlainText: z.string().optional().nullable(),
});
type UserNotesFormData = z.infer<typeof UserNotesSchema>;
